import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import {
  languageEditionAlternates,
} from "../data/siteNavigation";
import { seoConfig } from "../data/seoConfig";

const editionCopy = {
  es: {
    lang: "es",
    eyebrow: "Edición en español",
    title: "Robots humanoides, IA física y robótica",
    description:
      "Edición en español de TechniaHQRobot con guías sobre robots humanoides, IA física, IA incorporada, robótica y certificaciones.",
    lead:
      "Esta portada reúne las páginas disponibles en español y enlaza las secciones principales en inglés de TechniaHQRobot.",
    available: "Guías disponibles en español",
    availableDescription:
      "Estas páginas describen el alcance documental de varios programas de formación y examen. No constituyen un ranking basado en pruebas propias.",
    core: "Secciones principales en inglés",
    coreDescription:
      "El catálogo de robots, las noticias, los directorios de empresas y el centro de IA física siguen disponibles desde esta edición mientras continúa la traducción.",
    open: "Abrir la guía",
    englishNotice: "Contenido principal en inglés",
    certificationLinks: [
  [
    "Certificaciones relacionadas con la robótica humanoide",
    "/es/ai/best-humanoid-robot-certifications-2026"
  ],
  [
    "Physical AI: formación Isaac y certificación OpenUSD",
    "/es/ai/best-physical-ai-certifications-2026"
  ],
  [
    "Formación y certificaciones relacionadas con Embodied AI",
    "/es/ai/best-embodied-ai-certifications-2026"
  ],
  [
    "Certificaciones de robótica: qué formato de evaluación ofrecen",
    "/es/ai/best-robotics-certifications-2026"
  ]
],
  
"dateModified": "2026-09-29",
"independentEdition": true
},
  pt: {
    lang: "pt",
    independentEdition: true,
    eyebrow: "Edição em português",
    title: "Robôs humanoides, IA física e robótica",
    description:
      "Edição em português da TechniaHQRobot com guias sobre robôs humanoides, IA física, IA incorporada, robótica e certificações.",
    lead:
      "Esta página reúne o conteúdo disponível em português e conecta as principais seções técnicas da TechniaHQRobot. Cada guia mantém uma URL própria, uma identificação de idioma e um link para a versão original.",
    available: "Guias disponíveis em português",
    availableDescription:
      "Os quatro guias apresentam o conteúdo publicado de cursos e exames relacionados com robótica. A ordem dos programas não representa um ranking nem uma medição de resultados profissionais.",
    core: "Seções principais em inglês",
    coreDescription:
      "O catálogo de robôs, as notícias, os diretórios de empresas e o centro de IA física continuam acessíveis nesta edição enquanto a tradução avança.",
    open: "Abrir o guia",
    englishNotice: "Conteúdo principal em inglês",
    certificationLinks: [
      ["Formações e certificações em robôs humanoides", "/pt/ai/best-humanoid-robot-certifications-2026"],
      ["Formações e certificações em IA física", "/pt/ai/best-physical-ai-certifications-2026"],
      ["Formações e certificações em IA incorporada", "/pt/ai/best-embodied-ai-certifications-2026"],
      ["Formações e certificações em robótica", "/pt/ai/best-robotics-certifications-2026"],
    ],
  },
};

const coreLinks = [
  [{ es: "Catálogo de robots humanoides", pt: "Catálogo de robôs humanoides" }, "/humanoid-robots"],
  [{ es: "IA física", pt: "IA física" }, "/physical-ai"],
  [{ es: "Noticias de robótica", pt: "Notícias de robótica" }, "/robotics-news"],
  [{ es: "Empresas de robótica", pt: "Empresas de robótica" }, "/robotics-companies"],
  [{ es: "Software de robótica", pt: "Software de robótica" }, "/robotics-software"],
  [{ es: "Componentes de robots", pt: "Componentes de robôs" }, "/robot-components"],
  [{ es: "Sensores para robots", pt: "Sensores para robôs" }, "/robot-sensors"],
  [{ es: "Eventos de robótica", pt: "Eventos de robótica" }, "/robotics-events"],
];

export default function LocalizedEditionHome({ locale }) {
  const copy = editionCopy[locale] || editionCopy.es;
  const path = `/${locale}`;
  const pageUrl = `${seoConfig.baseUrl}${path}`;
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: copy.title,
      headline: copy.title,
      description: copy.description,
      url: pageUrl,
      inLanguage: copy.lang,
      dateModified: copy.dateModified || "2026-08-06",
      isPartOf: {
        "@type": "WebSite",
        name: "TechniaHQRobot",
        url: `${seoConfig.baseUrl}/`,
      },
      ...(copy.independentEdition ? {} : { translationOfWork: {
        "@type": "WebPage",
        url: `${seoConfig.baseUrl}/`,
        inLanguage: "en",
      } }),
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: copy.available,
      itemListElement: copy.certificationLinks.map(([name, href], index) => ({
        "@type": "ListItem",
        position: index + 1,
        name,
        url: `${seoConfig.baseUrl}${href}`,
      })),
    },
  ];

  return (
    <>
      <SEO
        path={path}
        title={`${copy.title} TechniaHQRobot`}
        description={copy.description}
        lang={copy.lang}
        alternates={languageEditionAlternates}
        schema={schema}
      />

      <div lang={copy.lang} className="pb-16">
        <section className="border-b border-neutral-200 bg-gradient-to-b from-blue-50 via-white to-white">
          <div className="site-shell py-12 sm:py-16">
            <p className="eyebrow-chip">{copy.eyebrow}</p>
            <h1 className="mt-5 max-w-5xl text-4xl font-black tracking-tight text-neutral-950 sm:text-6xl">
              {copy.title}
            </h1>
            <p className="mt-6 max-w-4xl text-lg leading-9 text-neutral-700">{copy.lead}</p>
          </div>
        </section>

        <section className="site-shell py-12 sm:py-16">
          <div className="max-w-4xl">
            <p className="eyebrow-chip">{copy.eyebrow}</p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-neutral-950 sm:text-4xl">{copy.available}</h2>
            <p className="mt-4 text-base leading-8 text-neutral-600">{copy.availableDescription}</p>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {copy.certificationLinks.map(([label, href]) => (
              <article key={href} className="soft-card soft-card-hover p-6">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">2026</p>
                <h3 className="mt-3 text-xl font-black leading-8 text-neutral-950">
                  <Link to={href} hrefLang={copy.lang} className="focus-ring rounded-sm hover:text-blue-700">
                    {label}
                  </Link>
                </h3>
                <Link to={href} hrefLang={copy.lang} className="mt-5 inline-flex text-sm font-black text-blue-700 hover:underline">
                  {copy.open}
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="border-y border-neutral-200 bg-neutral-50 py-12 sm:py-16">
          <div className="site-shell">
            <div className="max-w-4xl">
              <p className="eyebrow-chip">{copy.englishNotice}</p>
              <h2 className="mt-4 text-3xl font-black tracking-tight text-neutral-950 sm:text-4xl">{copy.core}</h2>
              <p className="mt-4 text-base leading-8 text-neutral-600">{copy.coreDescription}</p>
            </div>
            <nav className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label={copy.core}>
              {coreLinks.map(([label, href]) => (
                <Link
                  key={href}
                  to={href}
                  hrefLang="en"
                  className="focus-ring rounded-2xl border border-neutral-200 bg-white px-4 py-4 text-sm font-black text-neutral-800 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                >
                  {label[copy.lang]}
                </Link>
              ))}
            </nav>
          </div>
        </section>
      </div>
    </>
  );
}
